import Reveal from "@/components/site/Reveal";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import usePageMeta from "@/lib/usePageMeta";

export default function PrivacyPage() {
  usePageMeta({
    title: "Privacy Policy",
    description:
      "How Progressive Innovations LLC handles the information you share through this site, including contact form submissions.",
    path: "/privacy",
  });

  const updated = "December 2026";

  return (
    <div data-testid="page-privacy">
      <section>
        <div className="container-tight section-pad">
          <Reveal>
            <p className="overline">Legal</p>
            <h1
              data-testid="privacy-heading"
              className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em] text-brand-ink text-balance"
            >
              Privacy Policy
            </h1>
            <p className="mt-6 text-sm tracking-[0.18em] uppercase text-brand-muted">
              Last updated: {updated}
            </p>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <aside className="lg:col-span-3">
              <Reveal>
                <nav className="lg:sticky lg:top-32 space-y-3 text-sm">
                  {[
                    ["overview", "Overview"],
                    ["information", "Information we collect"],
                    ["use", "How we use information"],
                    ["sharing", "Sharing & disclosure"],
                    ["security", "Data security"],
                    ["retention", "Retention"],
                    ["rights", "Your rights"],
                    ["cookies", "Cookies & analytics"],
                    ["changes", "Changes to this policy"],
                    ["contact", "Contact"],
                  ].map(([id, label]) => (
                    <a
                      key={id}
                      href={`#${id}`}
                      className="block text-brand-muted hover:text-brand-accent transition-colors"
                    >
                      {label}
                    </a>
                  ))}
                </nav>
              </Reveal>
            </aside>

            <article className="lg:col-span-9 space-y-12 text-brand-muted leading-relaxed">
              <Reveal>
                <section id="overview">
                  <h2 className="font-display text-2xl tracking-tight text-brand-ink">Overview</h2>
                  <p className="mt-4">
                    Progressive Innovations LLC ("we," "our") operates this website to share information about our consulting services and to make it easy for prospective clients to get in touch. This policy explains what information we collect, how we use it, and the choices you have. We aim to collect as little personal information as possible — and only what's needed to respond to your inquiry.
                  </p>
                </section>
              </Reveal>

              <Reveal>
                <section id="information">
                  <h2 className="font-display text-2xl tracking-tight text-brand-ink">Information we collect</h2>
                  <p className="mt-4">When you submit the contact form, we collect:</p>
                  <ul className="mt-4 list-disc pl-6 space-y-2">
                    <li>Your name and email address</li>
                    <li>Your company and role (optional)</li>
                    <li>A description of your current challenge</li>
                    <li>Your preferred time window for a call (optional)</li>
                  </ul>
                  <p className="mt-4">
                    We may also collect standard technical information automatically through your browser (such as IP address, browser type, and pages viewed) for security and analytics purposes.
                  </p>
                </section>
              </Reveal>

              <Reveal>
                <section id="use">
                  <h2 className="font-display text-2xl tracking-tight text-brand-ink">How we use information</h2>
                  <p className="mt-4">We use the information you provide to:</p>
                  <ul className="mt-4 list-disc pl-6 space-y-2">
                    <li>Respond to your inquiry and schedule a discovery call</li>
                    <li>Communicate with you about a potential engagement</li>
                    <li>Keep records of business correspondence</li>
                    <li>Improve the content and performance of our website</li>
                  </ul>
                  <p className="mt-4">
                    We do not sell your information, and we do not send marketing emails unless you explicitly opt in.
                  </p>
                </section>
              </Reveal>

              <Reveal>
                <section id="sharing">
                  <h2 className="font-display text-2xl tracking-tight text-brand-ink">Sharing & disclosure</h2>
                  <p className="mt-4">
                    We do not share your personal information with third parties except in the following limited cases: (1) service providers who help us operate the site (e.g., hosting, email delivery, analytics) under appropriate confidentiality terms; (2) when required by law, regulation, or valid legal process; or (3) with your explicit consent.
                  </p>
                </section>
              </Reveal>

              <Reveal>
                <section id="security">
                  <h2 className="font-display text-2xl tracking-tight text-brand-ink">Data security</h2>
                  <p className="mt-4">
                    We use reasonable technical and organizational measures to protect the information you share with us, including encrypted transport (HTTPS) and access controls on backend systems. No method of transmission or storage is perfectly secure, however, and we cannot guarantee absolute security.
                  </p>
                </section>
              </Reveal>

              <Reveal>
                <section id="retention">
                  <h2 className="font-display text-2xl tracking-tight text-brand-ink">Retention</h2>
                  <p className="mt-4">
                    We retain contact form submissions for as long as necessary to respond to your inquiry and to maintain ordinary business records. You may request deletion at any time.
                  </p>
                </section>
              </Reveal>

              <Reveal>
                <section id="rights">
                  <h2 className="font-display text-2xl tracking-tight text-brand-ink">Your rights</h2>
                  <p className="mt-4">
                    Depending on where you live, you may have the right to access, correct, or request deletion of your personal information; to withdraw consent; or to object to certain processing. To exercise these rights, contact us at the email below.
                  </p>
                </section>
              </Reveal>

              <Reveal>
                <section id="cookies">
                  <h2 className="font-display text-2xl tracking-tight text-brand-ink">Cookies & analytics</h2>
                  <p className="mt-4">
                    This site may use a small number of cookies for essential functionality and lightweight analytics to understand how the site is used (such as which pages are visited). You can disable cookies in your browser settings; some features may not function as expected if you do.
                  </p>
                </section>
              </Reveal>

              <Reveal>
                <section id="changes">
                  <h2 className="font-display text-2xl tracking-tight text-brand-ink">Changes to this policy</h2>
                  <p className="mt-4">
                    We may update this policy from time to time. When we do, we'll revise the "Last updated" date at the top of this page. Material changes will be communicated through this site.
                  </p>
                </section>
              </Reveal>

              <Reveal>
                <section id="contact">
                  <h2 className="font-display text-2xl tracking-tight text-brand-ink">Contact</h2>
                  <p className="mt-4">
                    Questions about this policy or how we handle your information? Email{" "}
                    <a href="mailto:info@progressiveinn.com" className="text-brand-accent hover:underline">
                      info@progressiveinn.com
                    </a>
                    .
                  </p>
                </section>
              </Reveal>

              <Reveal>
                <div className="pt-8 border-t border-brand-line flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                  <p className="text-sm text-brand-subtle">
                    This is a summary policy. It is not legal advice. Consult counsel for jurisdiction-specific requirements.
                  </p>
                  <Link to="/contact" className="btn-primary self-start sm:self-auto" data-testid="privacy-cta">
                    Book a Discovery Call
                    <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                  </Link>
                </div>
              </Reveal>
            </article>
          </div>
        </div>
      </section>
    </div>
  );
}
